/**
 * Date Presets Component
 *
 * Quick date filter buttons for the dashboard.
 * Supports: Today, Last 7 Days, This Month, Last Month, This Quarter, YTD, All
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { Calendar, ChevronDown, Check } from 'lucide-react';
import { useSettings } from '../hooks/useSettings';
import { useTranslation } from '../hooks/useTranslation';

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const PresetButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${props => props.active ? '#3b82f6' : '#e2e8f0'};
  background: ${props => props.active ? '#eff6ff' : 'white'};
  color: ${props => props.active ? '#1d4ed8' : '#64748b'};

  &:hover {
    border-color: #3b82f6;
    background: ${props => props.active ? '#dbeafe' : '#f8fafc'};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
  }
`;

const AllDataButton = styled(PresetButton)`
  background: ${props => props.active ? '#f0fdf4' : 'white'};
  border-color: ${props => props.active ? '#22c55e' : '#e2e8f0'};
  color: ${props => props.active ? '#15803d' : '#64748b'};

  &:hover {
    border-color: #22c55e;
    background: ${props => props.active ? '#dcfce7' : '#f8fafc'};
  }
`;

const Divider = styled.div`
  width: 1px;
  height: 24px;
  background: #e2e8f0;
  margin: 0 4px;
`;

const CustomRangeWrapper = styled.div`
  position: relative;
`;

const CustomRangeDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  ${props => props.isRTL ? 'right: 0' : 'left: 0'};
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  padding: 16px;
  z-index: 100;
  min-width: 280px;
  border: 1px solid #e2e8f0;
`;

const DateInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DateInputLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: #64748b;
`;

const DateInput = styled.input`
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }
`;

const ApplyButton = styled.button`
  width: 100%;
  padding: 10px;
  margin-top: 12px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #2563eb;
  }

  &:disabled {
    background: #94a3b8;
    cursor: not-allowed;
  }
`;

const DATE_PRESETS = [
  { key: 'today', icon: null },
  { key: 'last7Days', icon: null },
  { key: 'thisMonth', icon: null },
  { key: 'lastMonth', icon: null },
  { key: 'thisQuarter', icon: null },
  { key: 'ytd', icon: null }
];

function DatePresets({ onChange }) {
  const { datePreset, startDate, endDate, setDatePreset, isRTL } = useSettings();
  const { t } = useTranslation();
  const [showCustom, setShowCustom] = useState(false);
  const [customStart, setCustomStart] = useState(startDate || '');
  const [customEnd, setCustomEnd] = useState(endDate || '');

  const handlePresetClick = (preset) => {
    setDatePreset(preset);
    setShowCustom(false);
    if (onChange) {
      onChange({ preset, startDate: null, endDate: null });
    }
  };

  const handleAllClick = () => {
    setDatePreset('all');
    setShowCustom(false);
    if (onChange) {
      onChange({ preset: 'all', startDate: null, endDate: null });
    }
  };

  const handleCustomToggle = () => {
    setShowCustom(!showCustom);
  };

  const handleApplyCustom = () => {
    if (customStart && customEnd) {
      setDatePreset('custom', customStart, customEnd);
      setShowCustom(false);
      if (onChange) {
        onChange({ preset: 'custom', startDate: customStart, endDate: customEnd });
      }
    }
  };

  const isValidCustomRange = customStart && customEnd && customStart <= customEnd;

  // Map preset keys to API values
  const presetApiMap = {
    today: 'today',
    last7Days: 'last_7_days',
    thisMonth: 'this_month',
    lastMonth: 'last_month',
    thisQuarter: 'this_quarter',
    ytd: 'ytd'
  };

  return (
    <Container>
      <Calendar size={16} color="#64748b" />

      {DATE_PRESETS.map(({ key }) => (
        <PresetButton
          key={key}
          active={datePreset === presetApiMap[key]}
          onClick={() => handlePresetClick(presetApiMap[key])}
        >
          {datePreset === presetApiMap[key] && <Check size={14} />}
          {t(`datePresets.${key}`)}
        </PresetButton>
      ))}

      <Divider />

      <CustomRangeWrapper>
        <PresetButton
          active={datePreset === 'custom'}
          onClick={handleCustomToggle}
        >
          {datePreset === 'custom' && <Check size={14} />}
          {t('datePresets.custom')}
          <ChevronDown size={14} />
        </PresetButton>

        {showCustom && (
          <CustomRangeDropdown isRTL={isRTL}>
            <DateInputGroup>
              <DateInputLabel>
                {t('common.from')}
                <DateInput
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                />
              </DateInputLabel>
              <DateInputLabel>
                {t('common.to')}
                <DateInput
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                />
              </DateInputLabel>
              <ApplyButton
                onClick={handleApplyCustom}
                disabled={!isValidCustomRange}
              >
                {t('action.apply')}
              </ApplyButton>
            </DateInputGroup>
          </CustomRangeDropdown>
        )}
      </CustomRangeWrapper>

      <Divider />

      <AllDataButton
        active={datePreset === 'all'}
        onClick={handleAllClick}
      >
        {datePreset === 'all' && <Check size={14} />}
        {t('datePresets.all')}
      </AllDataButton>
    </Container>
  );
}

export default DatePresets;
